import { useState, useEffect, useCallback } from "react";
import { getActiveDeliveryUsers } from "../api/user";
import useAuthScope from "./useAuthScope";

/**
 * Directorio compartido de repartidores (driverId -> "Name LastName").
 *
 * Envuelve `getActiveDeliveryUsers` (src/api/user.js) — la fuente de catálogo
 * YA existente — y la acota por `logisticCompanyId`, que se lee del JWT vía
 * `useAuthScope`. NO agrega endpoints ni cambia params: reusa la función tal
 * cual (solo derivación/presentación client-side).
 *
 * El fetch se cachea a nivel módulo por `logisticCompanyId`, de modo que
 * múltiples montajes (o múltiples consumidores) comparten un único request.
 * Los requests en vuelo también se comparten (de-dup de concurrencia).
 *
 * Ante cualquier error degrada a un Map vacío (sin cachearlo, para permitir
 * reintento en el próximo montaje); la UI consumidora cae al id crudo.
 *
 * @returns {{
 *   directory: Map<string, string>,  // driverId -> "Name LastName"
 *   getName: (id?: string) => (string|undefined),
 *   loading: boolean,
 * }}
 */

// Cache de directorios resueltos, keyed por logisticCompanyId (o '__all__').
const directoryCache = new Map(); // key -> Map<driverId, 'Name LastName'>
// Requests en vuelo, para de-duplicar fetches concurrentes.
const inflightCache = new Map(); // key -> Promise<Map<driverId, 'Name LastName'>>

const EMPTY_DIRECTORY = new Map();

const cacheKeyFor = (logisticCompanyId) => logisticCompanyId || "__all__";

// Normaliza las distintas formas en que el backend puede envolver el listado
// (array directo, { docs }, { users } o { data }). Mismo criterio que el
// fallback de getActiveDriversCount en src/api/user.js.
const extractDocs = (data) => {
  if (Array.isArray(data)) return data;
  return data?.docs || data?.users || data?.data || [];
};

const buildDisplayName = (user) => {
  const name = (user?.name || "").trim();
  const lastName = (user?.lastName || "").trim();
  const full = `${name} ${lastName}`.trim();
  return full || undefined;
};

const buildDirectory = (docs) => {
  const map = new Map();
  (docs || []).forEach((user) => {
    const id = user?._id || user?.id;
    if (!id) return;
    const display = buildDisplayName(user);
    if (display) map.set(String(id), display);
  });
  return map;
};

const fetchDirectory = (logisticCompanyId) => {
  const key = cacheKeyFor(logisticCompanyId);

  if (directoryCache.has(key)) {
    return Promise.resolve(directoryCache.get(key));
  }
  if (inflightCache.has(key)) {
    return inflightCache.get(key);
  }

  const request = (async () => {
    try {
      const res = await getActiveDeliveryUsers({ logisticCompanyId });
      const ok = res && res.success !== false && !res.error;
      if (!ok) {
        // Error suave (no thrown): degradar a Map vacío SIN cachear, para
        // permitir reintento en el próximo montaje.
        return EMPTY_DIRECTORY;
      }
      const directory = buildDirectory(extractDocs(res.data));
      directoryCache.set(key, directory);
      return directory;
    } catch (err) {
      // Error duro: degradar a Map vacío SIN cachear.
      return EMPTY_DIRECTORY;
    } finally {
      inflightCache.delete(key);
    }
  })();

  inflightCache.set(key, request);
  return request;
};

export default function useDriverDirectory() {
  const { logisticCompanyId } = useAuthScope();
  const key = cacheKeyFor(logisticCompanyId);

  const [directory, setDirectory] = useState(
    () => directoryCache.get(key) || EMPTY_DIRECTORY
  );
  const [loading, setLoading] = useState(() => !directoryCache.has(key));

  useEffect(() => {
    let active = true;

    const cached = directoryCache.get(key);
    if (cached) {
      setDirectory(cached);
      setLoading(false);
      return undefined;
    }

    setLoading(true);
    fetchDirectory(logisticCompanyId).then((map) => {
      if (!active) return;
      setDirectory(map);
      setLoading(false);
    });

    return () => {
      active = false;
    };
  }, [logisticCompanyId, key]);

  const getName = useCallback(
    (id) => {
      if (id === null || id === undefined) return undefined;
      return directory.get(String(id));
    },
    [directory]
  );

  return { directory, getName, loading };
}
